import { createSlice } from "@reduxjs/toolkit";
import { getPropertiesEntry, getPropertiesTemplateService } from "../requests";
import { qrSettingsInitialState } from "../../constants/common";

const qrSettings = createSlice({
  name: "qrSettings",
  initialState: qrSettingsInitialState,
  reducers: {
    qrPattern(state, { payload }) {
      state.pattern = payload;
    },
    qrPatternColor(state, { payload }) {
      state.patternColor = payload;
    },
    qrPatternBackground(state, { payload }) {
      state.patternBackground = payload;
    },
    qrPatternBackgroundTransparent(state, { payload }) {
      state.patternBackgroundTransparent = payload;
    },
    qrCornetsOuter(state, { payload }) {
      state.cornetsOuter = payload;
    },
    qrCornetsOuterColor(state, { payload }) {
      state.cornetsOuterColor = payload;
    },
    qrCornetsInterior(state, { payload }) {
      state.cornetsInterior = payload;
    },
    qrCornetsInteriorColor(state, { payload }) {
      state.cornetsInteriorColor = payload;
    },
    qrLogotype(state, { payload }) {
      state.logotype = payload;
    },
    qrLogoTypeSize(state, { payload }) {
      state.logotypeSize = payload;
    },
    qrLogoTypeMargin(state, { payload }) {
      state.logotypeMargin = payload;
    },
    qrLogoTypeBackground(state, { payload }) {
      state.logotypeBackground = payload;
    },
    qrSetSettings(state, { payload }) {
      return {
        ...qrSettingsInitialState,
        ...payload,
      };
    },
    qrResetSettings() {
      return qrSettingsInitialState;
    },
    setIsSingleColorDots(state, { payload }) {
      state.isSingleColorDots = payload;
    },
    resetQrPatternColor(state) {
      state.patternColor = qrSettingsInitialState.patternColor;
    },
    setGradientDotsColor(state, { payload }) {
      state.gradientDots.colorStops[payload.order].color = payload.color;
    },
    reverseGradientDots(state) {
      const reverse = [
        { offset: 0, color: state.gradientDots.colorStops[1].color },
        { offset: 1, color: state.gradientDots.colorStops[0].color },
      ];
      state.gradientDots.colorStops = reverse;
    },
    setGradientDotsType(state, { payload }) {
      state.gradientDots.type = payload;
    },
    setGradientDotsRotation(state, { payload }) {
      state.gradientDots.rotation = payload;
    },
    resetGradientDots(state) {
      state.gradientDots = qrSettingsInitialState.gradientDots;
    },
    setIsSingleColorExternalEye(state, { payload }) {
      state.isSingleColorExternalEye = payload;
    },
    reasetQrCornetsOuterColor(state) {
      state.cornetsOuterColor = qrSettingsInitialState.cornetsOuterColor;
    },
    setGradientExternalEyeColor(state, { payload }) {
      state.gradientExternalEye.colorStops[payload.order].color = payload.color;
    },
    reverseGradientExternalEyes(state) {
      const reverse = [
        { offset: 0, color: state.gradientExternalEye.colorStops[1].color },
        { offset: 1, color: state.gradientExternalEye.colorStops[0].color },
      ];
      state.gradientExternalEye.colorStops = reverse;
    },
    setGradientExternalEyeType(state, { payload }) {
      state.gradientExternalEye.type = payload;
    },
    setGradientExternalEyeRotation(state, { payload }) {
      state.gradientExternalEye.rotation = payload;
    },
    resetGradientExternalEyes(state) {
      state.gradientExternalEye = qrSettingsInitialState.gradientExternalEye;
    },
    setIsSingleColorInternalEye(state, { payload }) {
      state.isSingleColorInternalEye = payload;
    },
    resetQrCornetsInteriorColor(state) {
      state.cornetsInteriorColor = qrSettingsInitialState.cornetsInteriorColor;
    },
    setGradientInternalEyeColor(state, { payload }) {
      state.gradientInternalEye.colorStops[payload.order].color = payload.color;
    },
    reverseGradientInternalEyes(state) {
      const reverse = [
        { offset: 0, color: state.gradientInternalEye.colorStops[1].color },
        { offset: 1, color: state.gradientInternalEye.colorStops[0].color },
      ];
      state.gradientInternalEye.colorStops = reverse;
    },
    setGradientInternalEyeType(state, { payload }) {
      state.gradientInternalEye.type = payload;
    },
    setGradientInternalEyeRotation(state, { payload }) {
      state.gradientInternalEye.rotation = payload;
    },
    resetGradientInternalEyes(state) {
      state.gradientInternalEye = qrSettingsInitialState.gradientInternalEye;
    },
    setIsSingleColorBackground(state, { payload }) {
      state.isSingleColorBackground = payload;
    },

    setQrBackgroundColor(state, { payload }) {
      state.qrBackgroundColor = payload;
    },
    resetQrBackgroundColor(state) {
      state.qrBackgroundColor = qrSettingsInitialState.qrBackgroundColor;
    },
    setGradientBackgroundColor(state, { payload }) {
      state.gradientBackground.colorStops[payload.order].color = payload.color;
    },
    reverseGradientBackground(state) {
      const reverse = [
        { offset: 0, color: state.gradientBackground.colorStops[1].color },
        { offset: 1, color: state.gradientBackground.colorStops[0].color },
      ];
      state.gradientBackground.colorStops = reverse;
    },
    setGradientBackgroundType(state, { payload }) {
      state.gradientBackground.type = payload;
    },
    setGradientBackgroundRotation(state, { payload }) {
      state.gradientBackground.rotation = payload;
    },
    resetGradientBackground(state) {
      state.gradientBackground = qrSettingsInitialState.gradientBackground;
    },
    setBackgroundImage(state, { payload }) {
      state.backgroundImage = payload;
    },
    setBackgroundImageSize(state, { payload }) {
      state.backgroundImageSize = payload;
    },
    resetBackgroundImageSettings(state) {
      state.backgroundImage = qrSettingsInitialState.backgroundImage;
      state.backgroundImageSize = qrSettingsInitialState.backgroundImageSize;
    },
  },
  extraReducers: {
    [getPropertiesEntry.fulfilled]: (state, { payload }) => {
      if (payload.data.qrSettings) {
        return {
          ...qrSettingsInitialState,
          ...payload.data.qrSettings,
        };
      }
    },
    [getPropertiesTemplateService.fulfilled]: (state, { payload }) => {
      if (payload.data.qrSettings) {
        return {
          ...qrSettingsInitialState,
          ...payload.data.qrSettings,
        };
      }
    },
  },
});
export const {
  qrPattern,
  qrPatternColor,
  qrPatternBackground,
  qrPatternBackgroundTransparent,
  qrCornetsOuter,
  qrCornetsOuterColor,
  qrCornetsInterior,
  qrCornetsInteriorColor,
  qrLogotype,
  qrLogoTypeSize,
  qrLogoTypeMargin,
  qrLogoTypeBackground,
  qrSetSettings,
  qrResetSettings,
  setIsSingleColorDots,
  resetQrPatternColor,
  setGradientDotsColor,
  reverseGradientDots,
  setGradientDotsType,
  setGradientDotsRotation,
  resetGradientDots,
  setIsSingleColorExternalEye,
  reasetQrCornetsOuterColor,
  setGradientExternalEyeColor,
  reverseGradientExternalEyes,
  setGradientExternalEyeType,
  setGradientExternalEyeRotation,
  resetGradientExternalEyes,
  setIsSingleColorInternalEye,
  resetQrCornetsInteriorColor,
  setGradientInternalEyeColor,
  reverseGradientInternalEyes,
  setGradientInternalEyeType,
  setGradientInternalEyeRotation,
  resetGradientInternalEyes,
  setIsSingleColorBackground,
  setQrBackgroundColor,
  resetQrBackgroundColor,
  setGradientBackgroundColor,
  reverseGradientBackground,
  setGradientBackgroundType,
  setGradientBackgroundRotation,
  resetGradientBackground,
  setBackgroundImage,
  setBackgroundImageSize,
  resetBackgroundImageSettings,
} = qrSettings.actions;
export default qrSettings.reducer;
